import React, { useCallback, useEffect, useState } from 'react';

import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import ControlGroup from '@amzn/meridian/control-group';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';

// **** Types **** //

interface AssignModificationsModalProps {
  open: boolean;
  onClose: () => void;
  selectedTaskLabel: string | undefined;
  options: ModificationOption[];
  currentOptions: ModificationOption[];
  onSubmit: (value: ModificationOption[]) => void;
}

export interface ModificationOption {
  value: number;
  label: string;
}

// **** Components **** //

/**
 * Default component
 */
const AssignModificationsModal = ({
  open,
  onClose,
  selectedTaskLabel = '',
  options,
  currentOptions,
  onSubmit,
}: AssignModificationsModalProps) => {
  const [selectedOptions, setSelectedOptions] = useState<ModificationOption[]>([]);

  // Update local list when incoming list updates
  useEffect(() => {
    if (open) setSelectedOptions([...currentOptions]);
  }, [open, currentOptions]);

  // Update the selected list on checkbox click
  const onClickCheckbox = useCallback(
    (ids: number[]) => {
      const set = new Set(ids);
      const updatedList = options.filter((o) => set.has(o.value));
      setSelectedOptions(updatedList);
    },
    [options],
  );

  // Remove one modification
  const dismissTag = useCallback(
    (value: number) => {
      const newMods = selectedOptions.filter((m) => m.value != value);
      setSelectedOptions(newMods);
    },
    [selectedOptions],
  );

  return (
    <Modal open={open} title={`${selectedTaskLabel ?? ''} Modifications`} onClose={onClose}>
      {/* Header */}
      <Row alignmentHorizontal="justify">
        <Text>{selectedOptions.length} active</Text>
        <Button type="link" onClick={() => setSelectedOptions([])}>
          Clear All
        </Button>
      </Row>

      {/* Table  */}
      <Row spacingInset="none none 400 none" wrap="down" maxWidth="550px" spacing="300">
        {selectedOptions.map(({ label, value }) => (
          <Tag key={value} data-testid="modification-tag" type="neutral" onClose={() => dismissTag(value)}>
            {label}
          </Tag>
        ))}
      </Row>
      <Column type="outline" overflowY="scroll" minWidth="550px" maxHeight="400px" spacingInset="400">
        <ControlGroup value={selectedOptions.map((m) => m.value)} onChange={onClickCheckbox}>
          {options.map(({ value, label }) => (
            <Checkbox key={value} value={value}>
              {label}
            </Checkbox>
          ))}
        </ControlGroup>
      </Column>

      {/* Footer  */}
      <ModalFooter>
        <Row alignmentHorizontal="end">
          <Button type="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => onSubmit(selectedOptions)}>
            Apply
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

// **** Export **** //

export default AssignModificationsModal;
